/**
 * Historial de una celda del cuadro (008 · T045 · US1; contracts/ui.md §1.2, FR-005).
 *
 * `GET /schedule-grid/history?worker_id&day`: cada versión de la asignación del día (turno,
 * quién, cuándo, origen) y cuándo la reemplazó la siguiente; ninguna se borra. Se abre con clic
 * derecho sobre la celda, Enter con la celda enfocada o "Ver historial" en la barra de acción.
 *
 * DRIFT (wiring §13 "A VERIFICAR T045"): se muestra en el `Drawer` del Admin y no en un popover:
 * la lista de versiones puede ser larga y el drawer ya resuelve foco atrapado, Escape y lectura
 * accesible. El archivo conserva el nombre de tasks.md.
 *
 * 009 · US6 (T055): las versiones de origen integración dicen "Gestionado por <sistema>" y, si la
 * vigente es integrada, un aviso explica que el día se cambia en el sistema externo.
 */
import type { GridCellVersion } from '@fermin/contracts';

import { Badge } from '../../../components/ui/badge';
import { Drawer, DrawerContent } from '../../../components/ui/drawer';
import { EmptyState } from '../../../components/ui/empty-state';
import { SkeletonLines } from '../../../components/ui/skeleton';
import { formatDateTime, t } from '../../../i18n';
import { managedLabel, ManagedFieldHint, useIntegrationSystemName } from '../../integration/managed-by';
import { scheduleErrorMessage, useCellHistory, type GridCellRef } from '../api';
import { formatLocalDay, shiftsDescription } from '../format';

function versionContent(version: GridCellVersion): string {
  if (version.kind === 'REST') {
    return t('schedules.grid.cell.rest');
  }
  if (version.kind === 'OFF') {
    return t('schedules.grid.cell.off');
  }
  return shiftsDescription(version.shifts);
}

export interface CellHistoryPopoverProps {
  cell: GridCellRef | null;
  /** Nombre del trabajador de la celda (para el título). */
  workerName: string;
  /** Zona de la sede: fechas y horas del historial. */
  timeZone: string;
  onClose: () => void;
}

export function CellHistoryPopover({
  cell,
  workerName,
  timeZone,
  onClose,
}: CellHistoryPopoverProps) {
  const query = useCellHistory(cell);
  const versions = query.data?.versions ?? [];
  const systemName = useIntegrationSystemName();
  const currentIntegrated = versions.some(
    (version) => version.superseded_at === null && version.origin === 'INTEGRATION',
  );

  return (
    <Drawer open={cell !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DrawerContent
        title={t('schedules.history.title')}
        description={
          cell
            ? t('schedules.history.description', {
                worker: workerName,
                day: formatLocalDay(cell.day),
              })
            : undefined
        }
      >
        <div data-testid="schedule-cell-history" className="flex flex-col gap-fm-3">
          {query.isPending && cell !== null ? <SkeletonLines lines={3} /> : null}
          {query.isError ? (
            <p role="alert" className="text-fm-sm text-fm-danger-text">
              {scheduleErrorMessage(query.error)}
            </p>
          ) : null}
          {query.isSuccess && versions.length === 0 ? (
            <EmptyState title={t('schedules.history.empty')} />
          ) : null}
          {currentIntegrated ? (
            <div data-testid="schedule-cell-managed" className="flex flex-col gap-fm-1">
              {systemName ? <ManagedFieldHint systemName={systemName} /> : null}
              <p className="text-fm-xs text-fm-text-secondary">{t('schedules.managed.cellNotice')}</p>
            </div>
          ) : null}
          {versions.length > 0 ? (
            <ol className="flex flex-col gap-fm-3">
              {versions.map((version) => (
                <li
                  key={version.id}
                  data-testid="schedule-cell-history-version"
                  className="flex flex-col gap-fm-1 rounded-fm-md border border-fm-border p-fm-3"
                >
                  <div className="flex items-center justify-between gap-fm-2">
                    <span className="font-semibold text-fm-text">{versionContent(version)}</span>
                    {version.superseded_at === null ? (
                      <Badge variant="activa">{t('schedules.history.current')}</Badge>
                    ) : (
                      <Badge variant="reemplazada">{t('schedules.history.superseded')}</Badge>
                    )}
                  </div>
                  <span className="text-fm-xs text-fm-text-secondary">
                    {t('schedules.history.meta', {
                      when: formatDateTime(version.created_at, timeZone),
                      who: version.created_by?.name ?? t('schedules.history.system'),
                      origin:
                        version.origin === 'INTEGRATION'
                          ? managedLabel(systemName)
                          : t(`schedules.origin.${version.origin}`),
                    })}
                  </span>
                  {version.site ? (
                    <span className="text-fm-xs text-fm-text-secondary">
                      {t('schedules.history.site', { site: version.site.name })}
                    </span>
                  ) : null}
                  {version.superseded_at ? (
                    <span className="text-fm-xs text-fm-text-secondary">
                      {t('schedules.history.supersededAt', {
                        when: formatDateTime(version.superseded_at, timeZone),
                      })}
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
